import { Injectable } from '@angular/core'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { ChannelNameTokens, ConfirmDialogStatus, Toaster } from '@tokeet-frontend/tv3-platform'
import { DeleteInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { ConfirmDialogService, AlertDialogService } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import { Inquiry, isInhouseEligible } from '@tv3/store/inquiry/inquiry.model'
import { EMPTY, Observable, of } from 'rxjs'
import { concatMap, filter, map, switchMap, take, tap, toArray } from 'rxjs/operators'
import {
  ArchiveInquiries,
  FollowupInquiries,
  LinkInquiries,
  MarkInhouseInquiries,
  MarkNoInhouseInquiries,
  MarkPaidInquiries,
  MarkUnpaidInquiries,
  NoFollowupInquiries,
  ReadInquiries,
  UnArchiveInquiries,
  UnReadInquiries,
} from '@tv3/store/inquiry/inquiry-fields.actions'
import { selectConnectionByLinkedRentalId } from '@tv3/store/connection/connection.selectors'
import { ImportBookingFromABBV2Connection } from '@tv3/store/connection/connection.actions'
import { isActiveVrboBooking, isImportedFromABBV2, isVrboBooking } from '@tokeet-frontend/inquiry'

@Injectable()
export class InquiriesListService {
  constructor(
    private store: Store<fromRoot.State>,
    private toast: Toaster,
    private alertDialog: AlertDialogService,
    private confirmDialog: ConfirmDialogService
  ) {}

  link(ids: string[]): Observable<boolean> {
    if (ids.length <= 1) {
      this.alertDialog.open({
        title: $localize`No inquiries selected`,
        // tslint:disable-next-line:max-line-length
        body: $localize`Please select two or more inquiries that you would like to link. Once inquiries are linked all messages and invoices from every inquiries will be displayed in each inquiry. It is recommended that you archive all but one of the linked inquiries after linking.`,
      })
      return EMPTY
    }
    return this.confirmDialog
      .confirm({
        title: $localize`Link these inquiries?`,
        // tslint:disable-next-line:max-line-length
        body: 'Are you sure you want to link these inquiries? Once inquiries are linked all messages and invoices from every linked inquiry in the group will be displayed in each inquiry. It is recommended that you archive all but one of the linked inquiries after linking.<br/><br/>Why link inquiries? Sometimes a guest will correspond from multiple email addresses regarding the same inquiry, or different guests will email about the same inquiry. This may lead to many inquiry entries in AdvanceCM. The best way to resolve this is to link the associated inquiries and archive the ones which you do not plan to use.',
      })
      .pipe(
        tap(() => this.store.dispatch(LinkInquiries({ ids }))),
        map(() => true)
      )
  }

  importFromAirbnb(selected: Inquiry[]): Observable<any> {
    const inquiries = R.filter((i: Inquiry) => isImportedFromABBV2(i), selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one booking imported from Airbnb.`)
      return EMPTY
    }

    return of(...inquiries).pipe(
      concatMap((item) =>
        this.store.pipe(
          select(selectConnectionByLinkedRentalId(item.rentalId, ChannelNameTokens.AirBnBV2API)),
          take(1),
          tap((connections) => {
            const conn = R.head(connections)
            if (conn) {
              this.store.dispatch(
                ImportBookingFromABBV2Connection({ inquiryId: item.inquiryId, channelId: conn.channelId, data: conn })
              )
            } else {
              this.toast.warning(
                $localize`The associated rental "` + item.rentalView + $localize`" is not linked to any airbnb listing`,
                $localize`Unable to import`
              )
            }
          })
        )
      ),
      toArray()
    )
  }

  archive(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !i.archived, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one unarchived inquiry.`)
      return EMPTY
    }

    const activeVrboBookings = R.filter(isActiveVrboBooking, inquiries)
    if (activeVrboBookings.length > 0) {
      this.alertDialog.open({
        title: $localize`Action required in VRBO`,
        body: $localize`Your selection includes an active VRBO booking. Cancel it in VRBO and then mark it cancelled in AdvanceCM before archiving.`,
      })
      return EMPTY
    }

    const ids = R.map((i: Inquiry) => i.id, inquiries)
    const vrboWarning = R.any(isVrboBooking, inquiries)
      ? `<br><br>` + $localize`Archive VRBO bookings only after their cancellations are complete in VRBO.`
      : ''
    return this.confirmDialog
      .confirm({
        title: $localize`Archive selected inquiries?`,
        body: $localize`They will be removed from the default inbox view.` + vrboWarning,
        confirmText: $localize`Archive`,
        cancelText: $localize`Go back`,
        isDangerous: true,
      })
      .pipe(
        tap(() => this.store.dispatch(ArchiveInquiries({ ids }))),
        map(() => true)
      )
  }

  unArchive(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !!i.archived, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one archived inquiry.`)
      return EMPTY
    }
    const ids = R.map((i: Inquiry) => i.id, selected)
    this.store.dispatch(UnArchiveInquiries({ ids }))
    return of(true)
  }

  followUp(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !i.followup, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one inquiry to follow up.`)
      return EMPTY
    }
    const ids = R.map((i: Inquiry) => i.id, selected)
    this.store.dispatch(FollowupInquiries({ ids }))
    return of(true)
  }

  noFollowUp(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !!i.followup, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one inquiry to unfollow.`)
      return EMPTY
    }
    const ids = R.map((i: Inquiry) => i.id, selected)
    this.store.dispatch(NoFollowupInquiries({ ids }))
    return of(true)
  }

  read(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !i.read, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one inquiry that's unread.`)
      return EMPTY
    }
    const ids = R.map((i: Inquiry) => i.id, selected)
    this.store.dispatch(ReadInquiries({ ids }))
    return of(true)
  }

  unRead(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !!i.read, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one inquiry that's read.`)
      return EMPTY
    }
    const ids = R.map((i: Inquiry) => i.id, selected)
    this.store.dispatch(UnReadInquiries({ ids }))
    return of(true)
  }

  paid(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !i.paid, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one booked inquiry that's unpaid.`)
      return EMPTY
    }
    const anyNotBooked = R.any((i: Inquiry) => !i.cost, selected)
    if (anyNotBooked) {
      this.toast.warning($localize`Please de-select inquiries that are not booked.`)
      return EMPTY
    }
    this.store.dispatch(MarkPaidInquiries({ inquiries: selected }))
    return of(true)
  }

  unpaid(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !!i.paid, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one booked inquiry that's paid.`)
      return EMPTY
    }
    const anyNotBooked = R.any((i: Inquiry) => !i.cost, selected)
    if (anyNotBooked) {
      this.toast.warning($localize`Please de-select inquiries that are not booked.`)
      return EMPTY
    }
    this.store.dispatch(MarkUnpaidInquiries({ inquiries: selected }))
    return of(true)
  }

  inhouse(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !i.inhouse && isInhouseEligible(i), selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one booked inquiry that has reached its check-in date.`)
      return EMPTY
    }
    this.store.dispatch(MarkInhouseInquiries({ inquiries }))
    return of(true)
  }

  noInhouse(selected: Inquiry[]): Observable<boolean> {
    const inquiries = R.filter((i: Inquiry) => !!i.inhouse, selected)
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one inquiry that's marked as in-house.`)
      return EMPTY
    }
    this.store.dispatch(MarkNoInhouseInquiries({ inquiries }))
    return of(true)
  }

  delete(inquiries: Inquiry[]): Observable<boolean> {
    if (inquiries.length < 1) {
      this.toast.warning($localize`Please select at least one inquiry.`)
      return EMPTY
    }
    return this.confirmDialog
      .confirmExtra({
        title: $localize`Delete these inquiries?`,
        body:
          $localize`Are you sure you want to delete these inquiries / bookings?` +
          `<br>` +
          $localize`Once these are deleted this action cannot be undone. Please note that deleting confirmed bookings will not result in cancellation of the booking on the originating channel.`,
        extra: $localize`Also delete all guests associated with this inquiry.`,
      })
      .pipe(
        tap((result) => {
          this.store.dispatch(
            DeleteInquiries({
              inquiryIds: R.map((i) => i.id, inquiries),
              guestIds: R.map((i) => i.guestId, inquiries),
              guest: result.isChecked,
            })
          )
        }),
        map((result) => result.status === ConfirmDialogStatus.Confirmed)
      )
  }
}
